import { memo, useRef, useMemo, useCallback, useState, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Crud, CrudMode, notification, rejectionError, transformCrudFormErrors, getSelectsOptions } from '@ohpen/enso-ui';
import type { CrudFormComponent, CrudProps, SelectsOptions, ValidationError } from '@ohpen/enso-ui';
import {
  ChangeRequestOfExclusivitySetRequest,
  ExclusivitySetRequest,
  ExclusivitySetResponse,
  ExclusivitySetsCrudChangeResponse,
  productExclusivitySetsCreateChangeRequest,
} from '@pmp/api';
import { crudToActionMap, changeRequestNotificationMessage, changeRequestButtonMessage } from '@pmp/utils';
import { CrudFormProps } from '@pmp/types';
import { useFeatureToggle } from '@pmp/hooks';

import { getFormSchema } from '../formConfig';

export const ExclusiveProductsCrudForm = memo(
  ({ mode: modeProp, record, valueLists, refreshTable, onClose }: CrudFormProps<ExclusivitySetResponse>): ReactElement | null => {
    const ref = useRef<CrudFormComponent>(null);
    const [open, setOpen] = useState(true);
    const [mode, setMode] = useState<CrudMode | null>(modeProp);
    const { t } = useTranslation();
    const enableFourEyes = useFeatureToggle(['enableFourEyes']);

    const handleOnClose = useCallback(() => {
      setOpen(false);
      setTimeout(() => {
        onClose();
      }, 300);
    }, [onClose]);

    const onConfirm: (formData: ExclusivitySetRequest) => Promise<ValidationError[] | void> = useCallback(
      data => {
        if (!mode || !crudToActionMap[mode]) return Promise.reject(rejectionError('mode is required'));

        const body: ChangeRequestOfExclusivitySetRequest = {
          data,
          actionType: crudToActionMap[mode],
        };
        return productExclusivitySetsCreateChangeRequest({ body }).then(
          ({ data: { isValid, errors } }: { data: ExclusivitySetsCrudChangeResponse }) => {
            if (!isValid) {
              const form = ref.current?.form;
              if (form) {
                const errorFields = form.getFieldsError().map(f => ({ name: f.name, errors: [] }));
                if (errorFields.length) form.setFields(errorFields);
              }

              return transformCrudFormErrors(errors);
            }
            if (mode === CrudMode.EDIT) refreshTable();
            notification().success({
              message: changeRequestNotificationMessage(t, enableFourEyes),
            });
          },
        );
      },
      [mode, refreshTable, t, enableFourEyes],
    );

    const title = useMemo(() => {
      if (!mode) return '';
      const titles: { [key: string]: string } = {
        [CrudMode.VIEW]: t('global.labels.viewX', { x: t('pmp.exclusiveProducts.labels.exclusiveProducts') }),
        [CrudMode.CREATE]: t('global.labels.createX', { x: t('pmp.exclusiveProducts.labels.exclusiveProducts') }),
        [CrudMode.EDIT]: t('global.labels.editX', { x: t('pmp.exclusiveProducts.labels.exclusiveProducts') }),
      };
      return titles[mode];
    }, [mode, t]);

    const initialValues: ExclusivitySetRequest | null = useMemo(() => {
      if (!record) return null;
      const { lenderFirms, ...props } = record;
      return {
        ...props,
        lenderFirms: record.lenderFirms?.map(firm => firm.key) || [],
      };
    }, [record]);

    const crudProps: CrudProps<ExclusivitySetRequest> = useMemo(() => {
      const selectsOptions: SelectsOptions = (valueLists && getSelectsOptions(valueLists)) || {};

      return {
        ref,
        open: open,
        schema: getFormSchema(selectsOptions, mode),
        title,
        labels: {
          confirm: mode === CrudMode.EDIT ? t('global.labels.save') : changeRequestButtonMessage(t, enableFourEyes),
        },
        initialValues,
        mode,
        onClose: handleOnClose,
        onConfirm,
      };
    }, [valueLists, open, mode, title, t, initialValues, handleOnClose, onConfirm, enableFourEyes]);

    return <Crud {...crudProps} />;
  },
);
